<template>
  <div>
    <!-- Multi-link field (loop) -->
    <div v-if="field.isLinked && field.isMultiple">
      <div
        class="flex items-center gap-2 px-3 py-2 bg-purple-50 border-l-2 border-l-purple-400 rounded-md cursor-pointer hover:bg-purple-100"
        @click="$emit('toggle', field.variable + '_' + field.depth)"
      >
        <ChevronRight :class="['w-4 h-4 text-gray-600 transition-transform', expandedFields[field.variable + '_' + field.depth] && 'rotate-90']" />
        <List class="w-4 h-4 text-purple-600" />
        <span class="text-sm text-gray-700 flex-1">{{ field.name }}</span>
        <span class="text-xs text-purple-600 bg-purple-200 px-2 py-0.5 rounded">{{ t('loop') }}</span>
      </div>

      <div v-show="expandedFields[field.variable + '_' + field.depth]" class="ml-4 mt-2 flex flex-col gap-2 mb-2">
        <!-- Loop start -->
        <div
          class="flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-600"
          @click="$emit('copy', '#' + field.variable)"
        >
          <span class="text-sm text-blue-600 font-mono flex-1">{{ '{#' + field.variable + '}' }}</span>
          <div class="flex items-center gap-2">
            <span class="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded">{{ t('start') }}</span>
            <Check v-if="copiedVariable === '#' + field.variable" class="w-4 h-4 text-green-600" />
            <Copy v-else class="w-4 h-4 text-gray-400" />
          </div>
        </div>

        <!-- Nested fields inside loop -->
        <NestedFieldVariable
          v-for="child in field.nestedVariables"
          :key="child.variable"
          :field="child"
          :copied-variable="copiedVariable"
          :expanded-fields="expandedFields"
          @copy="$emit('copy', $event)"
          @toggle="$emit('toggle', $event)"
        />

        <!-- Loop end -->
        <div
          class="flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-600"
          @click="$emit('copy', '/' + field.variable)"
        >
          <span class="text-sm text-blue-600 font-mono flex-1">{{ '{/' + field.variable + '}' }}</span>
          <div class="flex items-center gap-2">
            <span class="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded">{{ t('end') }}</span>
            <Check v-if="copiedVariable === '/' + field.variable" class="w-4 h-4 text-green-600" />
            <Copy v-else class="w-4 h-4 text-gray-400" />
          </div>
        </div>
      </div>
    </div>

    <!-- Single-link field with nested children -->
    <div v-else-if="field.isLinked && !field.isMultiple && field.nestedVariables && field.nestedVariables.length > 0">
      <div
        class="flex items-center gap-2 px-3 py-2 bg-gray-50 border-l-2 border-l-blue-300 rounded-md cursor-pointer hover:bg-blue-50"
        @click="$emit('toggle', field.variable + '_' + field.depth)"
      >
        <ChevronRight :class="['w-4 h-4 text-gray-600 transition-transform', expandedFields[field.variable + '_' + field.depth] && 'rotate-90']" />
        <Link2 class="w-4 h-4 text-blue-600" />
        <span class="text-sm text-gray-700 flex-1">{{ field.name }}</span>
      </div>

      <div v-show="expandedFields[field.variable + '_' + field.depth]" class="ml-4 mt-2 flex flex-col gap-2">
        <NestedFieldVariable
          v-for="child in field.nestedVariables"
          :key="child.variable"
          :field="child"
          :copied-variable="copiedVariable"
          :expanded-fields="expandedFields"
          @copy="$emit('copy', $event)"
          @toggle="$emit('toggle', $event)"
        />
      </div>
    </div>

    <!-- Regular field (leaf node) -->
    <div
      v-else
      class="flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-600"
      @click="$emit('copy', field.variable)"
    >
      <span class="text-sm text-blue-600 font-mono flex-1">{{ '{' + field.variable + '}' }}</span>
      <Check v-if="copiedVariable === field.variable" class="w-4 h-4 text-green-600" />
      <Copy v-else class="w-4 h-4 text-gray-400" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChevronRight, Link2, List, Check, Copy } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface NestedField {
  variable: string
  name: string
  isLinked?: boolean
  isMultiple?: boolean
  depth: number
  nestedVariables?: NestedField[]
}

interface Props {
  field: NestedField
  copiedVariable: string | null
  expandedFields: Record<string, boolean>
}

withDefaults(defineProps<Props>(), {
  copiedVariable: null
})

defineEmits<{
  copy: [variable: string]
  toggle: [fieldKey: string]
}>()
</script>
